/**
 * Traffic-source classification from a raw referrer URL.
 *
 * 🚨 WHY THIS MODULE EXISTS (2026-08-16). Two copies of this rule existed — `AnalyticsTracker.tsx`
 * and `app/api/auth/insert-user/route.ts` — and BOTH matched `twitter.com` / `x.com` while neither
 * matched **`t.co`**, which is the domain X actually uses for every outbound link. So every visit X
 * ever sent us was filed as `other`.
 *
 * The cost was not theoretical: reading `referrer_domain` said X had sent us **1** session since the
 * mirror shipped. The raw `referrer_url` said **117**. A 100x under-read on the only channel that is
 * actually working, on the table anyone would naturally query first.
 *
 * ⚠ MATCHING IS ON THE PARSED HOSTNAME, NOT `includes()`. The old substring test would classify
 * `notx.com.evil.net` as X and `fake-reddit.com.co` as reddit. Suffix-match the hostname so a
 * referrer can only be attributed to a host that genuinely served it.
 *
 * ── 🚨 THREE MORE BUCKETS WERE WRONG, FOUND WHILE ENUMERATING THE BACKFILL (2026-08-16) ──
 * Every distinct referrer host in prod was listed before writing this. Three findings, all bigger
 * than the t.co bug they were found beside:
 *  1. **`accounts.google.com` is the OAuth CALLBACK, not a search referral** — 3,920 rows. Counting
 *     our own sign-in round-trip as Google acquisition inflated the single biggest "channel" we have.
 *     It is `auth` now. Same for `accounts.youtube.com`.
 *  2. **Internal navigation was `other`** — 13,751 rows of `thedesperatetrader.com`, by a wide margin
 *     the largest bucket in the table, and not an acquisition source at all. It is `internal` now.
 *  3. **Mobile apps send `android-app://` referrers with a package name for a hostname.**
 *     `com.reddit.frontpage` (477 rows) is the Reddit app and was landing in `other`, so Reddit was
 *     undercounted too — the fix here is not only pro-X.
 * ⚠ Between them those three were ~72% of all referrer rows, so the old `google` and `other` figures
 * should not be trusted for ANY period before the backfill.
 */

/** host, or any subdomain of it, e.g. `www.reddit.com` and `old.reddit.com` both match `reddit.com` */
function hostMatches(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith(`.${domain}`)
}

type Rule = { source: string; domains?: readonly string[]; test?: (hostname: string) => boolean }

/**
 * 🚨 ORDER IS SIGNIFICANT AND THE SPECIFIC RULES MUST COME FIRST. `accounts.google.com` has to be
 * tested before the general google rule or the OAuth callback is silently counted as search traffic,
 * which is exactly the error this table was rewritten to fix. Add new rules in the right band.
 */
const RULES: readonly Rule[] = [
  // ── 1. NOT ACQUISITION AT ALL. These must precede every vendor rule below. ──
  // Our own pages. The site host is matched by suffix so `www.` and any subdomain count.
  { source: 'internal', domains: ['thedesperatetrader.com'] },
  // OAuth sign-in round-trip. The user was already ours; Google/YouTube only brokered the login.
  { source: 'auth', test: (h) => h.startsWith('accounts.') },
  // Return leg from the payment flow, likewise ours.
  { source: 'billing', domains: ['stripe.com'] },
  // Webmail and mail apps: a click in an email we sent, not a search.
  // ⚠ `outlook.live.com` ends in `.live.com`, NOT `.outlook.com`, so it needs its own entry — the
  // kind of near-miss that put t.co in `other` for a month.
  { source: 'email', domains: ['mail.google.com', 'com.google.android.gm', 'mail.163.com', 'outlook.com', 'live.com', 'mail.yahoo.com'] },

  // ── 2. SOCIAL ──
  // `t.co` is X's link shortener and is what ACTUALLY appears in document.referrer for a click on a
  // post. `x.com` / `twitter.com` appear for profile and in-app navigation. All three are X.
  { source: 'twitter', domains: ['t.co', 'x.com', 'twitter.com', 'com.twitter.android', 'com.twitter.plus'] },
  // `com.reddit.frontpage` is the Reddit mobile app's android-app:// referrer.
  { source: 'reddit', domains: ['reddit.com', 'redd.it', 'com.reddit.frontpage'] },
  { source: 'facebook', domains: ['facebook.com', 'fb.com', 'com.facebook.katana'] },
  { source: 'instagram', domains: ['instagram.com', 'l.instagram.com'] },
  { source: 'linkedin', domains: ['linkedin.com', 'lnkd.in'] },
  { source: 'youtube', domains: ['youtube.com', 'youtu.be'] },
  // A real finance community that was sitting unlabelled in `other`.
  { source: 'stocktwits', domains: ['stocktwits.com'] },

  // ── 3. AI ASSISTANTS. Real traffic, all of it previously `other`. ──
  { source: 'chatgpt', domains: ['chatgpt.com', 'chat.openai.com', 'openai.com'] },
  { source: 'perplexity', domains: ['perplexity.ai'] },
  { source: 'copilot', domains: ['copilot.com', 'copilot.microsoft.com'] },
  { source: 'claude', domains: ['claude.ai'] },
  { source: 'doubao', domains: ['doubao.com'] },

  // ── 4. SEARCH. Google LAST among search so the specific google rules above win. ──
  { source: 'bing', domains: ['bing.com'] },
  { source: 'duckduckgo', domains: ['duckduckgo.com'] },
  { source: 'brave', domains: ['search.brave.com'] },
  { source: 'yahoo', domains: ['yahoo.com'] },
  { source: 'yandex', domains: ['yandex.com', 'yandex.ru'] },
  { source: 'baidu', domains: ['baidu.com'] },
  { source: 'ecosia', domains: ['ecosia.org'] },
  // ⭐ GOOGLE HAS ~200 COUNTRY DOMAINS and prod already holds google.co.za, .com.sg, .com.hk, .ca and
  // .pl. A `google.com`-only rule dropped every non-US searcher into `other`. Match the google.* and
  // *.google.* shapes instead, plus the Android search-app package.
  {
    source: 'google',
    test: (h) =>
      h === 'google.com' ||
      h.startsWith('google.') ||
      h.includes('.google.') ||
      h === 'com.google.android.googlequicksearchbox',
  },
]

/**
 * Returns the traffic-source label for a referrer URL.
 * `direct` when there is no referrer, `other` when it parses but matches nothing known.
 *
 * ⚠ `internal`, `auth` and `billing` are NOT acquisition sources. Exclude them when measuring where
 * users come from, or the answer is mostly our own site talking to itself.
 */
export function classifyTrafficSource(referrerUrl?: string | null): string {
  if (!referrerUrl) return 'direct'
  let hostname: string
  try {
    // ⭐ `new URL` handles `android-app://com.reddit.frontpage/` correctly — the package name comes
    // back as the hostname, which is what the app rules above match on.
    hostname = new URL(referrerUrl).hostname.toLowerCase().replace(/^www\./, '')
  } catch {
    // A referrer we cannot parse is not a referrer we can attribute. It is NOT `direct` — something
    // sent it — so keep it in `other` rather than inflating the direct bucket.
    return 'other'
  }
  if (!hostname) return 'direct'
  for (const rule of RULES) {
    if (rule.domains?.some((d) => hostMatches(hostname, d))) return rule.source
    if (rule.test?.(hostname)) return rule.source
  }
  return 'other'
}

/** Sources that are our own funnel rather than somewhere a user came FROM. */
export const NON_ACQUISITION_SOURCES = ['internal', 'auth', 'billing', 'direct'] as const

/** True when the referrer is our own site — an internal navigation, not an acquisition source. */
export function isInternalReferrer(referrerUrl: string | null | undefined, siteHost: string): boolean {
  if (!referrerUrl) return false
  try {
    return hostMatches(new URL(referrerUrl).hostname.toLowerCase().replace(/^www\./, ''), siteHost)
  } catch {
    return false
  }
}
